import { Dialog, DialogContent, DialogTitle } from '@/components/Dialog';
import { useCallback, useMemo, useState } from 'react';
import { Slide } from '../components/NewStyleFilter/OnBoarding/Slide';
import FilterOnboarding1 from '@/assets/images/generator/filter-onboarding-1.png';
import FilterOnboarding2 from '@/assets/images/generator/filter-onboarding-2.png';
import FilterOnboarding3 from '@/assets/images/generator/filter-onboarding-3.png';
import FilterOnboarding4 from '@/assets/images/generator/filter-onboarding-4.png';
import FilterOnboarding5 from '@/assets/images/generator/filter-onboarding-5.png';
import MarketPlaceIcon from '@/assets/icons/marketplace.svg';

import useInitializeData from '@/hooks/useInitializeProject';
import { FilterLibraryStateEnum, useGeneratorFilterStore } from '@/state/generator-filter-store';

export type OnboardingSlide = {
  title: string;
  description: string;
  image?: string;
  buttons?: Array<{
    label: string;
    variant?: 'default' | 'outline';
    onClick?: () => void;
  }>;
  cardContent?: React.ReactNode;
  currentSlide?: number;
  totalSlides?: number;
};

export type OnboardingProps = {
  onClose?: () => void;
};

export function useOnboarding({ onClose }: OnboardingProps = {}) {
  const [currentSlide, setCurrentSlide] = useState(0);

  const { userGuideManager } = useInitializeData();

  const { addFeature } = userGuideManager;

  const slides = [
    {
      title: (
        <span>
          Introducing StyleFilters<sub className="text-[24px] top-[-12px]">™</sub>
        </span>
      ),
      description:
        'Your sound. Their style. StyleFilter™ is our patented AI technology that lets you create your own music—“jenerations”—infused with the distinct style, vibe, feel, instrument textures, and structure of songs by your favorite artists and producers.',
      image: FilterOnboarding1,
    },
    {
      title: 'Where creativity meets ethics.',
      description:
        'This groundbreaking product innovation marks a historic first for AI music licensing and artist compensation. Every StyleFilter™ you buy pays the creators behind the sound.',
      image: FilterOnboarding2,
    },
    {
      title: (
        <span>
          Buy a StyleFilter<sub className="text-[24px] top-[-12px]">™</sub> & make music instantly.
        </span>
      ),
      description:
        'Each StyleFilter™ is available in the marketplace for $4.99 (low strength) or $7.99 (high strength), and can be applied to up to 90 minutes of “jenerations”—tracks created on Jen. More strength. More style.',
      image: FilterOnboarding3,
    },
    {
      title: 'You own your final jeneration.',
      description: (
        <div className="space-y-6">
          <div>
            Here’s the best part. Tracks created with StyleFilters™ are fully owned by the user—ready to be used, released, and shared
            across any project—but <span className="text-jenmusic-interactive">*not</span> to train other AI models.
          </div>
          <div className="text-jenmusic-interactive">*Don’t be unethical. </div>
        </div>
      ),
      image: FilterOnboarding4,
    },
    {
      title: (
        <div>
          <MarketPlaceIcon />
          <div className="mt-4">
            Shop the StyleFilter<sub className="text-[24px] top-[-12px]">™</sub> Marketplace.
          </div>
        </div>
      ),
      description:
        'Head over to the StyleFilter™ Marketplace to explore and shop. A dynamic lineup of new artist styles will continue to roll out—so check back often.',
      image: FilterOnboarding5,
    },
    // {
    //   title: 'Download and Jenerous license.',
    //   description:
    //     'Lorem ipsum dolor sit amet consectetur adipiscing elit Ut et massa mi. Aliquam in hendrerit urna. Pellentesque sit amet sapien.',
    //   buttons: [
    //     {
    //       label: 'Shop Filters',
    //       onClick: () => {
    //         addFeature('style_filter_onboarding');
    //         hide();
    //       },
    //     },
    //     {
    //       label: 'StyleFilter Library',
    //       variant: 'outline',
    //       onClick: () => {
    //         addFeature('style_filter_onboarding');
    //         hide();
    //       },
    //     },
    //   ],
    // },
  ] as OnboardingSlide[];

  const totalSlides = slides.length;

  const [open, setOpen] = useState(false);

  const nextSlide = () => {
    if (currentSlide < totalSlides - 1) {
      setCurrentSlide(currentSlide + 1);
    }
  };

  const previousSlide = () => {
    if (currentSlide > 0) {
      setCurrentSlide(currentSlide - 1);
    }
  };

  const show = useCallback(() => {
    setOpen(true);
  }, [setOpen]);

  const hide = useCallback(() => {
    setOpen(false);
    onClose?.();
  }, [setOpen, onClose]);

  const FilterOnboarding = useMemo(
    () =>
      function FilterDialog() {
        const { setCurrentState } = useGeneratorFilterStore();
        return (
          <Dialog
            open={open}
            onOpenChange={(open) => {
              setOpen(open);
              if (!open) {
                onClose?.();
              }
            }}
          >
            <DialogContent
              onInteractOutside={(e) => e.preventDefault()}
              closeClassName="hidden"
              className="w-[720px] max-w-7xl"
              closePosition="right"
            >
              <div className="min-h-[480px] w-full bg-white rounded-xl overflow-hidden">
                <Slide
                  {...slides[currentSlide]}
                  currentSlide={currentSlide + 1}
                  totalSlides={totalSlides}
                  onNext={() => {
                    if (currentSlide === totalSlides - 1) {
                      addFeature('style_filter_onboarding');
                      setOpen(false);
                    } else {
                      nextSlide();
                    }
                  }}
                  onPrevious={() => {
                    if (currentSlide > 0) {
                      previousSlide();
                    }
                  }}
                />
              </div>
            </DialogContent>
          </Dialog>
        );
      },
    [open, currentSlide, totalSlides, nextSlide, setOpen, onClose],
  );

  return {
    show,
    hide,
    FilterOnboarding,
    open,
    currentSlide,
    totalSlides,
    nextSlide,
    previousSlide,
    isLastSlide: currentSlide === totalSlides - 1,
    currentSlideData: slides[currentSlide],
  };
}
